import * as React from 'react';
import { Button, Dropdown, Modal, ModalBody, ModalFooter, ModalTitle, } from '@symphony-ui/uitoolkit-components';
import { sendFetchError } from 'utils/messageUtils';
import ApiServerRequest from 'models/ApiServerRequest';
import DownloadContext from 'contexts/DownloadContext';
import BookrunnerPicker from './BookrunnerPicker';
import TrancheWrapper from '../../../../../../models/TrancheWrapper';
import Bookrunner from '../../../../../../models/meta/Bookrunner';
const BookrunnerEditModal = ({ bookrunnerIndex, callback, initialBookrunner, onClose, parentNode, trancheIndex, transactionId, roleOptions }) => {
    const { apiFetchJson } = React.useContext(DownloadContext);
    const [partyId, setPartyId] = React.useState(initialBookrunner.override?.partyId ?? initialBookrunner.partyId);
    const [name, setName] = React.useState(initialBookrunner.override?.name ?? initialBookrunner.name ?? '');
    const [roles, setRoles] = React.useState(initialBookrunner.override?.roles ?? initialBookrunner.roles ?? []);
    const [loadingSave, setLoadingSave] = React.useState(false);
    const [loading, setLoading] = React.useState(new Map());
    const onClick = React.useCallback((type) => () => {
        const formData = new FormData();
        formData.append('transactionId', transactionId);
        formData.append('trancheIndex', trancheIndex.toString());
        formData.append('bookrunnerIndex', bookrunnerIndex.toString());
        const request = new ApiServerRequest('/syndicate/meta/bookrunners/' + type, { body: formData, method: 'PATCH' });
        setLoading(() => {
            const newMap = new Map([['ignore', true], ['remove', true], ['delete', true]]);
            newMap.set(type, false);
            return newMap;
        });
        (async () => {
            try {
                const body = await apiFetchJson(request);
                const tranches = body.map(t => new TrancheWrapper(t));
                callback(tranches);
                onClose();
            }
            catch (error) {
                sendFetchError("Unable to save ignore bookrunner", error, request);
            }
            finally {
                setLoading(new Map([['ignore', false], ['remove', false], ['delete', false]]));
            }
        })();
    }, [apiFetchJson, bookrunnerIndex, callback, onClose, trancheIndex, transactionId]);
    const onClickSave = React.useCallback(() => {
        const formData = new FormData();
        formData.append('transactionId', transactionId);
        formData.append('trancheIndex', trancheIndex.toString());
        formData.append('bookrunnerIndex', bookrunnerIndex.toString());
        const updatedBookrunner = new Bookrunner({
            override: {
                partyId: partyId,
                name: name,
                roles: roles
            },
            names: initialBookrunner.names
        });
        if (initialBookrunner.roles === roles) {
            updatedBookrunner.roles = initialBookrunner.roles;
        }
        formData.append('bookrunner', new Blob([JSON.stringify(updatedBookrunner)], { type: 'application/json' }));
        const request = new ApiServerRequest('/syndicate/meta/bookrunners/save', { body: formData, method: 'PATCH' });
        setLoadingSave(true);
        (async () => {
            try {
                const body = await apiFetchJson(request);
                const tranches = body.map(t => new TrancheWrapper(t));
                callback(tranches);
                onClose();
            }
            catch (error) {
                sendFetchError("Unable to save ignore bookrunner", error, request);
            }
            finally {
                setLoading(new Map([['ignore', false], ['remove', false], ['delete', false]]));
            }
        })();
    }, [apiFetchJson, bookrunnerIndex, callback, initialBookrunner.names, initialBookrunner.roles, name, onClose, partyId, roles, trancheIndex, transactionId]);
    const onClearParty = React.useCallback(() => {
        setPartyId(undefined);
    }, []);
    const onChangeRoles = React.useCallback((event) => {
        const value = event.target.value;
        const newValue = Array.isArray(value) ? value.filter(v => 'value' in v).map(v => v.value) : ('value' in value ? [value.value] : []);
        setRoles(newValue);
    }, []);
    const onChangeParty = React.useCallback((event) => {
        setPartyId(event.target.value.id);
        setName(event.target.value.name);
    }, []);
    const disabledDropdownRoles = React.useMemo(() => initialBookrunner.partyId !== undefined, [initialBookrunner.partyId]);
    const disabledDropdownBookrunner = React.useMemo(() => initialBookrunner.partyId !== undefined && initialBookrunner.override?.partyId === undefined, [initialBookrunner.override?.partyId, initialBookrunner.partyId]);
    const disabledSave = React.useMemo(() => partyId === undefined || roles.length === 0, [partyId, roles.length]);
    const disabledRemove = React.useMemo(() => loading.get('delete') ?? loading.get('ignore') ?? loadingSave, [loading, loadingSave]);
    const disabledIgnore = React.useMemo(() => loading.get('delete') ?? loading.get('remove') ?? loadingSave, [loading, loadingSave]);
    const disabledDelete = React.useMemo(() => loading.get('ignore') ?? loading.get('remove') ?? loadingSave, [loading, loadingSave]);
    return (React.createElement(Modal, { className: 'cmo-dropdown-absolute', closeButton: true, onClose: onClose, parentNode: parentNode, show: true, size: 'medium' },
        React.createElement(ModalTitle, null, "Edit Bookrunner"),
        React.createElement(ModalBody, null,
            initialBookrunner.names?.join(", "),
            React.createElement(BookrunnerPicker, { disabled: disabledDropdownBookrunner, initialNames: initialBookrunner.names, initialPartyId: partyId, onChangeParty: onChangeParty }),
            React.createElement(Dropdown, { isDisabled: disabledDropdownRoles, isInputClearable: true, isMultiSelect: true, label: "Roles", name: "names", onChange: onChangeRoles, onClear: onClearParty, options: roleOptions, value: roleOptions.filter(o => 'value' in o && roles.includes(o.value)) })),
        React.createElement(ModalFooter, null,
            React.createElement("div", { style: { display: 'flex', justifyContent: 'right' } },
                React.createElement("div", { style: { marginRight: '0.5rem' } },
                    React.createElement(Button, { disabled: disabledSave, loading: loadingSave, onClick: onClickSave }, "SAVE")),
                initialBookrunner.override !== undefined ?
                    (React.createElement("div", { style: { marginRight: '0.5rem' } },
                        React.createElement(Button, { disabled: disabledRemove, loading: loading.get('remove'), onClick: onClick('remove') }, "REMOVE"))) : undefined,
                initialBookrunner.names !== undefined ?
                    React.createElement(Button, { disabled: disabledIgnore, loading: loading.get('ignore'), onClick: onClick('ignore') }, "IGNORE")
                    :
                        React.createElement(Button, { disabled: disabledDelete, loading: loading.get('delete'), onClick: onClick('delete') }, "DELETE")))));
};
export default BookrunnerEditModal;
